import type { EngineRuleVersion, HotelResult } from '@niarab2c/frontend-commons/src/types/hotels';
import { createAsyncThunk, createSlice, PayloadAction, unwrapResult } from '@reduxjs/toolkit';
import _isEqual from 'lodash/isEqual';
import _isEqualWith from 'lodash/isEqualWith';
import { nanoid } from 'nanoid';
import { Filter } from '@niaratech/devcase-crud';
import type { BaseRootState } from '../../base';
import * as Hotel from '../hotel';
import { AvailableFilterOptions } from '../../../util/hotels';
import { AvailableSortOptions, buildAvailableFilterOptions, buildAvailableSortOptions, roomFilter } from '../../../util/hotels/filterResults';
import type { CriteriaForm, State as HotelSearchState } from './hotelSearch';
import hotelSearch, { searchHotels as _searchHotels } from './hotelSearch';
export interface HotelDetailsState {
  hotelSearch?: HotelSearchState;
  hotelId?: string;
  hotelResult?: HotelResult;
  filter?: Filter;
  count?: number;
  availableFilterOptions?: AvailableFilterOptions;
  availableSortOptions?: AvailableSortOptions;
}
const isCriteriaEqual = function (a: CriteriaForm, b: CriteriaForm) {
  const tester = (objValue, othValue, key): boolean => {
    if (key === 'destinationName') {
      return true; //ignora destinationName na comparação
    }
    return _isEqual(objValue, othValue);
  };
  return _isEqualWith(a, b, tester);
};
export const searchHotels = createAsyncThunk<void, {
  hotelId: string;
  propertyId?: string;
  criteriaForm: CriteriaForm & {
    user?: any;
  };
  engineRuleVersion: EngineRuleVersion;
  isForGroups?: boolean;
  locale?: string;
}, {
  state: BaseRootState;
  rejectValue: {
    searchId: string;
    errorMessage: string;
  };
}>('hotelDetails/searchHotels', async ({
  criteriaForm,
  hotelId,
  propertyId,
  engineRuleVersion,
  isForGroups,
  locale
}, {
  getState,
  dispatch,
  rejectWithValue
}) => {
  dispatch(_setHotelId({
    hotelId
  }));
  if (!criteriaForm) {
    _setHotelSearch(undefined);
  } else {
    criteriaForm = {
      ...criteriaForm,
      bestOnly: false
    };
    criteriaForm.destinations = Object.assign<(typeof criteriaForm)['destinations'], (typeof criteriaForm)['destinations']>({
      hotelIds: [hotelId].filter(Boolean)
    }, propertyId ? {
      propertyId,
      contentType: 'property'
    } : {});

    //Dados da pesquisa anterior
    const currentHotelSearch = getState().hotel?.hotelDetails?.hotelSearch;
    const currentCriteria = currentHotelSearch?.criteria;
    const currentEngineRuleVersion = currentHotelSearch?.engineRuleVersion;
    //pega filtro definido no search
    const filterFromSearch = getState().hotel.hotelPagination?.filter;
    const hasFilterFromSearch = filterFromSearch && Object.keys(filterFromSearch).length > 0;
    if (hasFilterFromSearch) {
      dispatch(setFilter({
        filter: filterFromSearch as Filter
      }));
    }

    // compara o critério e engineRuleVersion - se não mudou, não faz nova busca
    if (currentCriteria && isCriteriaEqual(currentCriteria, criteriaForm) && engineRuleVersion == currentEngineRuleVersion) {
      // keep previous search
    } else {
      const searchId = nanoid();
      await dispatch(Hotel.loadHotelSearchRule({
        clientId: criteriaForm.clientId,
        propertyId,
        isForGroups
      }));

      // pega o showUnavailableRates aqui para não fazer duas vezes a busca,
      // pois senão só depois da primeira busca que teria o valor do  showUnavailableRates
      // #211396
      const showUnavailableRates = getState().hotel.hotelSearchRule?.showUnavailableRates;
      const modules_crs = getState().core.tenant.modules_crs;
      const availRatesOnly = showUnavailableRates === true ? false : showUnavailableRates === false ? true : modules_crs ? false : true; /** Por padrão, central de reservas mostra motivos de não retorno */

      dispatch(_setHotelSearch({
        hotelSearch: {
          searchId,
          performance: []
        }
      }));
      try {
        await dispatch(_searchHotels({
          criteriaForm: {
            ...criteriaForm,
            availRatesOnly
          },
          searchId,
          engineRuleVersion,
          analyticsEventType: 'hotelAvailability',
          isForGroups,
          locale
        })).then(unwrapResult /** Vai expor o exception - assim, HotelDetails consegue detectar que deu erro e reencaminha para a tela de pesquisa */);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return rejectWithValue({
          searchId,
          errorMessage: error.errorMessage
        });
      }
    }
  }
});
const hotelDetails = createSlice({
  name: 'hotelDetails',
  initialState: {
    hotelSearch: hotelSearch(undefined, {
      type: 'IGNORE'
    }),
    filter: {},
    count: 0,
    availableFilterOptions: [],
    availableSortOptions: []
  } as HotelDetailsState,
  reducers: {
    _setHotelSearch: (state, action: PayloadAction<{
      hotelSearch?: HotelSearchState;
    }>) => {
      state.hotelSearch = action.payload.hotelSearch;
    },
    _setHotelId: (state, action: PayloadAction<{
      hotelId?: string;
    }>) => {
      if (state.hotelId !== action.payload.hotelId) {
        return {
          hotelId: action.payload.hotelId
        };
      }
      return state;
    },
    _setHotelResult: (state, action: PayloadAction<{
      hotelResult?: HotelResult;
    }>) => {
      state.hotelResult = action.payload.hotelResult;
    },
    setFilter: (state, action: PayloadAction<{
      filter: Filter;
    }>) => {
      if (!_isEqual(state.filter, action.payload.filter)) {
        state.filter = action.payload.filter;
        if (state.hotelResult) {
          // limpar filtro
          if (Object.keys(action.payload.filter).length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {
              _roomRates,
              _notAvailableRoomRates,
              ...restHotelResult
            } = state.hotelResult;
            state.hotelResult = {
              ...restHotelResult
            };
            state.count = 0;
          } else {
            const filterFunc = roomFilter(action.payload.filter);
            const filteredRoomRates = state.hotelResult.roomRates?.filter(filterFunc) || [];
            const filteredNotAvailableRoomRates = state.hotelResult.notAvailableRoomRates?.filter((item: any) => filterFunc(item)) || [];
            state.hotelResult = {
              ...state.hotelResult,
              _roomRates: filteredRoomRates,
              _notAvailableRoomRates: filteredNotAvailableRoomRates
            };
            state.count = filteredRoomRates.length + filteredNotAvailableRoomRates.length;
          }
        }
      }
    }
  },
  extraReducers: builder => {
    builder.addCase(searchHotels.fulfilled, (state, action) => {
      const hotelResult = state.hotelSearch.results ? Object.values(state.hotelSearch.results).find(hr => hr._soupIds.indexOf(state.hotelId) >= 0) : null;
      state.hotelResult = hotelResult;
      if (hotelResult) {
        state.availableFilterOptions = buildAvailableFilterOptions([], [hotelResult], false, true);
        state.availableSortOptions = buildAvailableSortOptions([], [hotelResult], true);
        if (state.filter && Object.keys(state.filter).length > 0) {
          const filterFunc = roomFilter(state.filter);
          const filteredRoomRates = hotelResult.roomRates?.filter(filterFunc) || [];
          const filteredNotAvailableRoomRates = hotelResult.notAvailableRoomRates?.filter((item: any) => filterFunc(item)) || [];
          state.hotelResult = {
            ...hotelResult,
            _roomRates: filteredRoomRates,
            _notAvailableRoomRates: filteredNotAvailableRoomRates
          };
          state.count = filteredRoomRates.length + filteredNotAvailableRoomRates.length;
        } else {
          state.count = 0;
        }
      }
    }).addCase(searchHotels.rejected, (state, action) => {
      state.hotelSearch.status = 'complete';
      state.hotelSearch.criteria = undefined;
      state.hotelSearch.errorMessage = action.payload?.errorMessage;
    }).addMatcher(action => action.type.startsWith('hotelSearch'), (state, action) => {
      // Sempre processar searchHotels.pending ou quando os searchIds batem
      if (action.type === 'hotelSearch/searchHotels/pending' || state?.hotelSearch?.searchId === action.payload?.searchId || state?.hotelSearch?.searchId === action.meta?.arg?.searchId) {
        // Se for o início de uma nova busca, garantir que o performance seja zerado
        const currentHotelSearch = action.type === 'hotelSearch/searchHotels/pending' ? {
          ...state.hotelSearch,
          performance: []
        } : state.hotelSearch;
        const newHotelSearch = hotelSearch(currentHotelSearch, action);
        if (state.hotelSearch === newHotelSearch) return state;
        const hotelResult = newHotelSearch.results ? Object.values(newHotelSearch.results).find(hr => hr._soupIds.indexOf(state.hotelId) >= 0) : undefined;
        let result = {
          ...state,
          hotelSearch: newHotelSearch,
          hotelResult
        };
        if (hotelResult) {
          state.availableFilterOptions = buildAvailableFilterOptions([], [hotelResult], false, true);
          state.availableSortOptions = buildAvailableSortOptions([], [hotelResult], true);
          result = {
            ...result,
            availableFilterOptions: state.availableFilterOptions,
            availableSortOptions: state.availableSortOptions
          };
          if (state.filter && Object.keys(state.filter).length > 0) {
            const filterFunc = roomFilter(state.filter);
            const filteredRoomRates = hotelResult.roomRates?.filter(filterFunc) || [];
            const filteredNotAvailableRoomRates = hotelResult.notAvailableRoomRates?.filter((item: any) => filterFunc(item)) || [];
            const activeFiltersCount = filteredRoomRates.length === hotelResult.roomRates.length;
            result.hotelResult = {
              ...hotelResult,
              _roomRates: filteredRoomRates,
              _notAvailableRoomRates: filteredNotAvailableRoomRates
            };
            result.count = activeFiltersCount ? 0 : filteredRoomRates.length + filteredNotAvailableRoomRates.length;
          } else {
            result.count = 0;
          }
        }
        return result;
      }
      return state;
    });
  }
});
const {
  _setHotelSearch,
  _setHotelId,
  setFilter
} = hotelDetails.actions;
export default hotelDetails.reducer;
export { setFilter };